import { c as _c } from "react/compiler-runtime";
import { type Ref, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BUSINESS_INSIGHT_SUBSECTIONS, type SubsectionType } from '@/components/entity-onboarding-details';
import { ONBOARDING_SECTION_SCROLL_EVENT, type OnboardingSectionScrollRequest } from '../../onboardings-shared/hooks/use-show-onboarding';
import { pinScrollPosition } from '../../onboardings-shared/utils';
import type { SectionKey } from '../onboardings.types';

/** The gap between sections, which a section landing below the sticky strip keeps from it too. */
const SECTION_GAP = 16;
type Landing = {
  key: SectionKey;
} | 'top';

/**
 * Lands a switch just below the sticky strip: View current at Current's matching section, with focus on it, and a pick
 * made partway down the panel at the next panel's top.
 */
const useSectionScroll = (selectedId, selectedSection) => {
  const $ = _c(20);
  const stripRef = useRef(null);
  const panelRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const sectionsRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const refCallbacksRef = useRef(t1);
  const landingRef = useRef(null);
  const [scrollRequest, setScrollRequest] = useState();
  const handledRequestRef = useRef(undefined);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const handleScrollRequest = event => {
        if (!(event instanceof CustomEvent)) {
          return;
        }
        event.preventDefault();
        setScrollRequest((event as CustomEvent<OnboardingSectionScrollRequest>).detail);
      };
      window.addEventListener(ONBOARDING_SECTION_SCROLL_EVENT, handleScrollRequest);
      return () => window.removeEventListener(ONBOARDING_SECTION_SCROLL_EVENT, handleScrollRequest);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = key => {
      const existing = refCallbacksRef.current.get(key);
      if (existing) {
        return existing;
      }
      const callback = node => {
        if (node) {
          sectionsRef.current.set(key, node);
        } else {
          sectionsRef.current.delete(key);
        }
      };
      refCallbacksRef.current.set(key, callback);
      return callback;
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const sectionRef = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => stripRef.current?.getBoundingClientRect().bottom ?? 0;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const getStripBottom = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const strip = stripRef.current;
      const scroller = strip?.closest("main");
      if (!strip || !scroller) {
        return getStripBottom();
      }
      const {
        borderTopWidth,
        paddingTop
      } = getComputedStyle(scroller);
      const contentTop = scroller.getBoundingClientRect().top + Number.parseFloat(borderTopWidth) + Number.parseFloat(paddingTop);
      const stickyTop = Number.parseFloat(getComputedStyle(strip).top);
      return Number.isNaN(stickyTop) ? contentTop : contentTop + stickyTop + strip.offsetHeight;
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const getStuckStripBottom = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setScrollRequest(undefined);
      const panelTop = panelRef.current?.getBoundingClientRect().top ?? 0;
      landingRef.current = panelTop < getStripBottom() ? "top" : null;
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const openAtTop = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = key_0 => {
      setScrollRequest(undefined);
      landingRef.current = {
        key: key_0
      };
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const openAt = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const landing = landingRef.current;
      landingRef.current = null;
      if (landing === "top") {
        return panelRef.current ? pinScrollPosition(panelRef.current, getStripBottom()) : undefined;
      }
      const section = landing && sectionsRef.current.get(landing.key);
      if (!section) {
        return;
      }
      section.focus({
        preventScroll: true
      });
      return pinScrollPosition(section, getStuckStripBottom() + SECTION_GAP);
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== selectedId) {
    t10 = [selectedId];
    $[10] = selectedId;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  useLayoutEffect(t9, t10);
  let t11;
  if ($[12] !== scrollRequest || $[13] !== selectedId) {
    t11 = () => {
      if (!scrollRequest || scrollRequest.id !== selectedId || handledRequestRef.current === scrollRequest) {
        return;
      }
      const key_2 = [...sectionsRef.current.keys()].find(key_1 => key_1 === "business-insights" ? BUSINESS_INSIGHT_SUBSECTIONS.some(section_0 => section_0 === scrollRequest.subsection) : key_1 === scrollRequest.subsection);
      const section_1 = key_2 && sectionsRef.current.get(key_2);
      if (!section_1) {
        return;
      }
      handledRequestRef.current = scrollRequest;
      section_1.focus({
        preventScroll: true
      });
      const isSticky = stripRef.current && getComputedStyle(stripRef.current).position === "sticky";
      const tabsHeight = isSticky ? Number.parseFloat(getComputedStyle(section_1).getPropertyValue("--onboardings-tabs-height")) || 0 : 0;
      return pinScrollPosition(section_1, getStuckStripBottom() + tabsHeight + SECTION_GAP);
    };
    $[12] = scrollRequest;
    $[13] = selectedId;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== scrollRequest || $[16] !== selectedId || $[17] !== selectedSection) {
    t12 = [scrollRequest, selectedId, selectedSection];
    $[15] = scrollRequest;
    $[16] = selectedId;
    $[17] = selectedSection;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  useLayoutEffect(t11, t12);
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      stripRef,
      panelRef,
      sectionRef,
      openAt,
      openAtTop
    };
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  return t13;
};
export default useSectionScroll;